/**
 * TermsPage - Användarvillkor
 *
 * SEO Target: /villkor
 * Legal page - should be indexed but with lower priority
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { FileText, ChevronRight } from "lucide-react";
import { useCompanyInfo } from "@/hooks/use-company-info";

export function TermsPage() {
  const lastUpdated = "2026-02-01";
  const { company, formattedCityOnly } = useCompanyInfo();

  // Format org number for display
  const orgNumberDisplay = company.orgNumber || "559XXX-XXXX";

  const breadcrumbItems = [{ label: "Användarvillkor" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Användarvillkor", url: "/villkor" },
  ]);

  const sections = [
    { id: "om-tjansten", title: "1. Om Tjänsten" },
    { id: "konto", title: "2. Konto och Registrering" },
    { id: "anvandning", title: "3. Användning av Tjänsten" },
    { id: "betalning", title: "4. Betalning och Priser" },
    { id: "ansvar", title: "5. Ansvar för Innehåll" },
    { id: "immateriella", title: "6. Immateriella Rättigheter" },
    { id: "sekretess", title: "7. Sekretess och Databehandling" },
    { id: "begransning", title: "8. Ansvarsbegränsning" },
    { id: "uppsagning", title: "9. Uppsägning" },
    { id: "andringar", title: "10. Ändringar av Villkoren" },
    { id: "tvister", title: "11. Tvister och Tillämplig Lag" },
    { id: "kontakt", title: "12. Kontakt" },
  ];

  return (
    <>
      <PageMeta
        title="Användarvillkor - Kvanten"
        description="Läs Kvantens användarvillkor för tjänsten digital årsredovisning, aktiebok och protokollhantering."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/villkor`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Användarvillkor</h1>
              <p className="text-gray-500">Senast uppdaterad: {lastUpdated}</p>
            </div>
          </div>

          <p className="text-lg text-gray-600">
            Dessa användarvillkor gäller för din användning av Kvantens tjänster. Genom att använda
            Tjänsten godkänner du dessa villkor.
          </p>
        </div>
      </section>

      {/* Content with sidebar */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-12">
            {/* Table of contents - Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-gray-50 rounded-xl p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Innehåll</h2>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors py-1"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {section.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <article className="lg:col-span-3 prose prose-gray max-w-none">
              <p className="lead">
                Dessa användarvillkor ("Villkoren") gäller för din användning av Kvantens tjänster
                ("Tjänsten") som tillhandahålls av {company.name}{orgNumberDisplay ? `, org.nr ${orgNumberDisplay}` : ""} ("Kvanten", "vi",
                "oss"). Genom att använda Tjänsten godkänner du dessa Villkor.
              </p>

              <section id="om-tjansten">
                <h2>1. Om Tjänsten</h2>

                <h3>1.1 Beskrivning</h3>
                <p>
                  Kvanten tillhandahåller en digital plattform för att skapa årsredovisningar enligt
                  K2- och K3-regelverken, hantera digital aktiebok enligt aktiebolagslagen, samt
                  generera protokoll för bolagsstämmor. Tjänsten inkluderar:
                </p>
                <ul>
                  <li>Import och analys av SIE-filer från bokföringsprogram</li>
                  <li>Automatisk generering av årsredovisning i PDF- och XML-format</li>
                  <li>Digital aktiebok med ägarförteckning</li>
                  <li>Automatiskt genererade årsstämmoprotokoll</li>
                  <li>Validering mot gällande regelverk (BFNAR 2016:10, ÅRL, ABL)</li>
                </ul>

                <h3>1.2 Tillgänglighet</h3>
                <p>
                  Tjänsten erbjuds till juridiska personer registrerade i Sverige som uppfyller
                  kraven för att tillämpa K2- eller K3-regelverket. Fysiska personer och enskilda
                  firmor omfattas inte av Tjänsten.
                </p>
              </section>

              <section id="konto">
                <h2>2. Konto och Registrering</h2>

                <h3>2.1 Kontouppgifter</h3>
                <p>
                  För att använda Tjänsten måste du skapa ett konto. Du ansvarar för att:
                </p>
                <ul>
                  <li>Lämna korrekta och fullständiga uppgifter vid registrering</li>
                  <li>Hålla dina inloggningsuppgifter säkra och konfidentiella</li>
                  <li>Omedelbart meddela oss vid misstänkt obehörig användning av ditt konto</li>
                  <li>All aktivitet som sker under ditt konto</li>
                </ul>

                <h3>2.2 Behörig företrädare</h3>
                <p>
                  Genom att använda Tjänsten för ett företags räkning intygar du att du är behörig
                  företrädare för bolaget eller har fullmakt att agera på bolagets vägnar.
                </p>
              </section>

              <section id="anvandning">
                <h2>3. Användning av Tjänsten</h2>

                <h3>3.1 Licens</h3>
                <p>
                  Vi ger dig en begränsad, icke-exklusiv, icke-överlåtbar rätt att använda Tjänsten
                  i enlighet med dessa Villkor.
                </p>

                <h3>3.2 Tillåten användning</h3>
                <p>Du får använda Tjänsten för att:</p>
                <ul>
                  <li>Skapa årsredovisningar för företag du är behörig att företräda</li>
                  <li>Hantera aktiebok för företag du är behörig att företräda</li>
                  <li>Generera protokoll för bolagsstämmor</li>
                  <li>Exportera och arkivera dokument</li>
                </ul>

                <h3>3.3 Förbjuden användning</h3>
                <p>Du får inte:</p>
                <ul>
                  <li>Använda Tjänsten för olagliga ändamål</li>
                  <li>Försöka få obehörig åtkomst till Tjänsten eller andra användares data</li>
                  <li>Kopiera, modifiera eller distribuera Tjänstens programvara</li>
                  <li>Använda automatiserade verktyg för att skrapa eller extrahera data</li>
                  <li>Överföra skadlig kod eller störa Tjänstens funktionalitet</li>
                  <li>Använda Tjänsten för att skapa falska eller vilseledande dokument</li>
                </ul>
              </section>

              <section id="betalning">
                <h2>4. Betalning och Priser</h2>

                <h3>4.1 Prissättning</h3>
                <p>
                  Tjänsten är kostnadsfri att använda fram till export. Betalning sker per företag
                  och räkenskapsår vid export av färdig årsredovisning. Aktuella priser visas på{" "}
                  <Link to="/priser" className="text-blue-600 hover:underline">
                    vår prissida
                  </Link>
                  .
                </p>

                <h3>4.2 Betalningsvillkor</h3>
                <p>
                  Betalning sker via de betalningsmetoder som erbjuds i Tjänsten. Alla priser anges
                  exklusive moms (25%) om inte annat anges. Faktura betalas inom 30 dagar från
                  fakturadatum.
                </p>

                <h3>4.3 Återbetalning</h3>
                <p>
                  Enligt distansavtalslagen har du som konsument 14 dagars ångerrätt. Ångerrätten
                  upphör dock att gälla när du har påbörjat nedladdning av den färdiga
                  årsredovisningen (PDF/XML). Vid tekniska fel som förhindrar användning av
                  exporterade filer erbjuder vi återbetalning eller omgenerering.
                </p>
              </section>

              <section id="ansvar">
                <h2>5. Ansvar för Innehåll</h2>

                <h3>5.1 Ditt ansvar</h3>
                <p>
                  Du ansvarar för att de uppgifter du lämnar till Tjänsten är korrekta och
                  fullständiga. Kvanten ansvarar inte för fel i årsredovisningen som beror på
                  felaktiga uppgifter i din SIE-fil eller information du angett manuellt.
                </p>

                <h3>5.2 Kvantens ansvar</h3>
                <p>
                  Kvanten tillhandahåller verktyg för att skapa årsredovisningar men ersätter inte
                  professionell redovisningsrådgivning. Vi ansvarar för att Tjänsten följer gällande
                  tekniska specifikationer för K2 och K3, men det slutliga ansvaret för
                  årsredovisningens innehåll ligger hos bolagets styrelse.
                </p>

                <h3>5.3 Ingen revision</h3>
                <p>
                  Tjänsten utför automatisk validering mot K2/K3-regelverket men utgör inte
                  revision. Om ditt bolag har revisionsplikt måste årsredovisningen fortfarande
                  granskas av en godkänd eller auktoriserad revisor.
                </p>

                <h3>5.4 Kvantens friskrivningar</h3>
                <p>
                  Kvanten är ett digitalt verktyg som automatiserar skapandet av årsredovisningar
                  baserat på data du tillhandahåller. Kvanten ansvarar uttryckligen <strong>inte</strong> för:
                </p>
                <ul>
                  <li>Felaktigheter i din SIE-fil eller annan data du laddar upp</li>
                  <li>Felaktig information du anger manuellt i tjänsten</li>
                  <li>Felaktig klassificering av konton i ditt bokföringsprogram</li>
                  <li>Ofullständig eller missvisande bokföring</li>
                  <li>Val av fel redovisningsregelverk (K2/K3)</li>
                  <li>Felaktiga notvärden eller upplysningar du anger</li>
                  <li>Brister i styrelseuppgifter, firmatecknare eller ägarinformation</li>
                  <li>Att du missar deadlines för inlämning till Bolagsverket</li>
                  <li>Skattemässiga konsekvenser av felaktigheter i årsredovisningen</li>
                  <li>Beslut baserade på årsredovisningen (utdelning, krediter, etc.)</li>
                </ul>
                <p>
                  Genom att använda Tjänsten bekräftar du att du förstår att det slutgiltiga
                  ansvaret för årsredovisningens innehåll och riktighet alltid ligger hos
                  bolagets styrelse enligt aktiebolagslagen.
                </p>
              </section>

              <section id="immateriella">
                <h2>6. Immateriella Rättigheter</h2>

                <h3>6.1 Kvantens rättigheter</h3>
                <p>
                  Tjänsten och dess innehåll (exklusive användardata) är skyddade av upphovsrätt,
                  varumärken och andra immateriella rättigheter som tillhör Kvanten. Du får inte
                  kopiera, modifiera eller använda vårt varumärke utan skriftligt tillstånd.
                </p>

                <h3>6.2 Dina rättigheter</h3>
                <p>
                  Du behåller alla rättigheter till den data och de dokument du skapar med hjälp av
                  Tjänsten. Du ger Kvanten en begränsad licens att lagra och bearbeta din data i
                  syfte att tillhandahålla Tjänsten.
                </p>
              </section>

              <section id="sekretess">
                <h2>7. Sekretess och Databehandling</h2>

                <p>
                  Vår behandling av personuppgifter beskrivs i vår{" "}
                  <Link to="/integritetspolicy" className="text-blue-600 hover:underline">
                    Integritetspolicy
                  </Link>
                  . Genom att använda Tjänsten godkänner du vår behandling av personuppgifter i
                  enlighet med Integritetspolicyn.
                </p>
              </section>

              <section id="begransning">
                <h2>8. Ansvarsbegränsning</h2>

                <h3>8.1 Tjänstens tillgänglighet</h3>
                <p>
                  Vi strävar efter hög tillgänglighet men garanterar inte att Tjänsten alltid är
                  tillgänglig eller felfri. Planerat underhåll aviseras i förväg när så är möjligt.
                </p>

                <h3>8.2 Skadestånd</h3>
                <p>
                  Kvantens totala ansvar för skador orsakade av Tjänsten är begränsat till det
                  belopp du betalat för Tjänsten under de senaste 12 månaderna. Vi ansvarar inte för
                  indirekta skador, utebliven vinst eller följdskador.
                </p>

                <h3>8.3 Force majeure</h3>
                <p>
                  Vi ansvarar inte för förseningar eller fel som orsakas av omständigheter utanför
                  vår kontroll, såsom naturkatastrofer, krig, myndighetsåtgärder eller störningar i
                  internet.
                </p>
              </section>

              <section id="uppsagning">
                <h2>9. Uppsägning</h2>

                <h3>9.1 Uppsägning av dig</h3>
                <p>
                  Du kan när som helst avsluta ditt konto via kontoinställningarna. Vid uppsägning
                  har du rätt att exportera din data under en rimlig övergångsperiod (30 dagar).
                </p>

                <h3>9.2 Uppsägning av oss</h3>
                <p>
                  Vi kan säga upp eller suspendera ditt konto om du bryter mot dessa Villkor eller
                  om vi har anledning att misstänka bedrägligt beteende. Vid allvarliga
                  överträdelser kan uppsägning ske med omedelbar verkan.
                </p>

                <h3>9.3 Efter uppsägning</h3>
                <p>
                  Vid uppsägning upphör din rätt att använda Tjänsten. Redan betalda avgifter
                  återbetalas inte, förutom vid uppsägning från vår sida utan giltig anledning.
                </p>
              </section>

              <section id="andringar">
                <h2>10. Ändringar av Villkoren</h2>

                <p>
                  Vi kan uppdatera dessa Villkor från tid till annan. Vid väsentliga ändringar
                  meddelar vi dig via e-post eller i Tjänsten minst 30 dagar före ändringarna träder
                  i kraft. Fortsatt användning av Tjänsten efter att ändringar trätt i kraft innebär
                  att du accepterar de nya Villkoren.
                </p>
              </section>

              <section id="tvister">
                <h2>11. Tvister och Tillämplig Lag</h2>

                <p>
                  Dessa Villkor regleras av svensk lag. Tvister ska i första hand lösas genom
                  förhandling. Om parterna inte kan enas ska tvisten avgöras av svensk allmän
                  domstol med Stockholms tingsrätt som första instans.
                </p>
              </section>

              <section id="kontakt">
                <h2>12. Kontakt</h2>

                <p>Om du har frågor om dessa Villkor, kontakta oss på:</p>
                <div className="bg-gray-50 rounded-lg p-4 not-prose">
                  <p className="text-gray-700">
                    <strong>E-post:</strong>{" "}
                    <a
                      href={`mailto:${company.email}`}
                      className="text-blue-600 hover:underline"
                    >
                      {company.email}
                    </a>
                  </p>
                  <p className="text-gray-700 mt-2">
                    <strong>Adress:</strong> {company.name}, {formattedCityOnly}
                  </p>
                </div>
              </section>

              <hr className="my-8" />

              <div className="bg-blue-50 rounded-lg p-4 not-prose">
                <p className="text-sm text-blue-800">
                  Genom att skapa ett konto eller använda Tjänsten bekräftar du att du har läst,
                  förstått och accepterar dessa Användarvillkor.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
